import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { uid } from "@/lib/uid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Loader2 } from "lucide-react";
import MansioneCard from "./MansioneCard";

export default function StepMansioni({ dvr, data, onChange, aiLoading, setAiLoading }) {
  const list = Array.isArray(data) ? data : [];
  const [nuova, setNuova] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [filter, setFilter] = useState("");
  const s1 = dvr.steps?.step1_dati_generali || {};

  const addMansione = async () => {
    if (!nuova.trim()) return;
    setAiLoading(true);
    try {
      const r = await api.post("/dvr/ai/mansione", {
        mansione: nuova.trim(),
        descrizione: descrizione.trim() || undefined,
        ateco: s1.ateco,
        tipo_attivita: s1.tipo_attivita,
      });
      onChange([...list, { mansione: nuova.trim(), _uid: uid(), ...r.data }]);
      setNuova("");
      setDescrizione("");
      toast.success(`Mansione "${nuova.trim()}" analizzata`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    }
    setAiLoading(false);
  };

  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const updateItem = (i, next) => onChange(list.map((it, idx) => (idx === i ? next : it)));

  const visible = filter.trim()
    ? list.filter((m) => m.mansione?.toLowerCase().includes(filter.toLowerCase()))
    : list;

  return (
    <div className="space-y-4" data-testid="step-mansioni">
      <p className="text-sm text-muted-foreground">
        Inserisci le mansioni presenti in azienda. Per ogni mansione puoi aggiungere una <b>descrizione libera</b>{" "}
        che verrà passata all'AI per una valutazione più contestualizzata. Ogni risultato è <b>modificabile
        manualmente</b> (rischi con P×D, DPI, formazione, sorveglianza sanitaria, misure, procedura).
      </p>

      {/* Form input mansione */}
      <div className="border border-border rounded-md p-3 bg-muted/20 space-y-3">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Aggiungi mansione</div>
        <div>
          <Label className="text-xs">Nome mansione</Label>
          <Input
            value={nuova}
            onChange={(e) => setNuova(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !aiLoading && !e.shiftKey && addMansione()}
            placeholder="Es. Videoterminalista, Addetto pulizie, Autista, Saldatore…"
            data-testid="s4-nuova"
          />
        </div>
        <div>
          <Label className="text-xs">Descrizione mansione / attività svolta (opzionale)</Label>
          <Textarea
            rows={3}
            placeholder="Es. saldatore MIG/MAG su carpenteria pesante, 6h/giorno, prevalentemente su acciai al carbonio, ambiente con aspirazione localizzata…"
            value={descrizione}
            onChange={(e) => setDescrizione(e.target.value)}
            data-testid="s4-descrizione"
          />
          <p className="text-[10px] text-muted-foreground mt-1">
            La descrizione aiuta l'AI a calibrare rischi, DPI e formazione sulle reali condizioni operative.
          </p>
        </div>
        <Button
          onClick={addMansione}
          disabled={aiLoading || !nuova.trim()}
          data-testid="s4-add-ai"
          className="rounded-full"
        >
          {aiLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          Aggiungi con AI
        </Button>
      </div>

      {list.length > 3 && (
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Cerca tra le mansioni…"
          className="max-w-sm"
          data-testid="s4-filter"
        />
      )}

      <div className="space-y-3">
        {visible.map((m) => {
          const originalIdx = list.indexOf(m);
          return (
            <MansioneCard
              key={m._uid || `mans-${m.mansione}-${originalIdx}`}
              item={m}
              index={originalIdx}
              onSave={(next) => updateItem(originalIdx, next)}
              onRemove={() => remove(originalIdx)}
            />
          );
        })}
        {list.length === 0 && (
          <div className="text-sm text-muted-foreground italic border border-dashed border-border rounded p-6 text-center">
            Nessuna mansione ancora inserita.
          </div>
        )}
      </div>
    </div>
  );
}
